import React, { memo, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import PaneBody from 'components/Common/Pane/PaneBody'
import Expander from 'components/utils/Expander'
import Clickable from 'components/Common/Clickable'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import LineBedDoubleIcon from 'LuxKit/Icons/line/LineBedDoubleIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'
import clsx from 'clsx'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getImageUrl } from 'lib/image/imageUtils'
import { IMAGE_HOST_CONFIG } from 'components/OfferPage/TourV2/TourV2HostsSponsors'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import TourItineraryExperienceCard from 'components/OfferPage/TourV2/Itinerary/TourItineraryExperienceCard'
import Group from 'components/utils/Group'
import { partitionBy } from 'lib/array/arrayUtils'
import LabeledSheet from 'LuxKit/LabeledSheet'
import LineBusIcon from 'LuxKit/Icons/line/LineBusIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import LineSubwayIcon from 'LuxKit/Icons/line/LineSubwayIcon'
import LineWalkingIcon from 'LuxKit/Icons/line/LineWalkingIcon'
import SvgIcon from 'LuxKit/SvgIcon'
import switchFunc from 'lib/function/switchFunc'
import { useIntl } from 'react-intl'

const BASE_RADIUS = 10
const STROKE_WIDTH = 2

const AccordionSection = styled.div`
  &:last-child {
    > * {
      border-bottom-color: transparent;
    }
  }
`
const AccordionHeader = styled(Clickable)`
  display: flex;
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(20)} 0;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};

  ${mediaQueryUp.tablet} {
    background-color: ${props => props.theme.palette.neutral.default.seven};
    border-radius: ${props => props.theme.borderRadius.S};
    border-bottom: unset;
    padding: ${rem(12)};
  }

  &.open {
    border-bottom-color: transparent;
  }
`

const DescriptionWrapper = styled(Group)`
  padding: 0 0 ${rem(32)} 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} 0;
    border-bottom: none;
  }
`

const PaneBodyStyled = styled(PaneBody)`
  width: 100%;
  ${mediaQueryUp.tablet} {
    padding: 0 ${rem(24)} 0 ${rem(18)};
  }
`

const VisualAccentWrapper = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    align-items: center;
    flex-direction: column;
    width: ${rem(20)};
  }
`

const DashedLine = styled.div`
  height: 100%;
  border: 1px dashed ${props => props.theme.palette.neutral.default.five};
`

const CircleWrapper = styled.div`
  margin-top: ${rem(4)};
  filter: drop-shadow(0px 0.7px 1.4px rgba(0, 0, 0, 0.07)) drop-shadow(0px 1.9px 4px rgba(0, 0, 0, 0.05)) drop-shadow(0px 4.5px 10px rgba(0, 0, 0, 0.05));
`

const TitleWrapper = styled.div`
  display: grid;
  grid-template:
    "dayLabel duration"
    "title title"
    / min-content;
  grid-column-gap: ${rem(12)};
  grid-row-gap: ${rem(4)};

  ${mediaQueryUp.tablet} {
    grid-template:
    "dayLabel title" 1fr
    "dayLabel duration" auto
    / auto 1fr;
    grid-row-gap: 0;
  }
`

const Title = styled(Heading)`
  grid-area: title;
`

const DurationWrapper = styled(Group)`
  grid-area: duration;
`

const DayLabel = styled(StructureLabel)`
  grid-area: dayLabel;
  align-self: center;
`

const ItineraryIcon = styled(LineAngleRightIcon)`
  transform: rotate(90deg);
  transition: transform 0.2s ease-out;

  &.open {
    transform: rotate(-90deg);
  }
`

const StopCircle = styled.circle`
  fill: ${props => props.theme.palette.neutral.default.one};
  stroke: ${props => props.theme.palette.neutral.default.eight};
`

const SpecialEventWrapper = styled(Group)`
  padding: ${rem(8)} ${rem(16)};
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
`

const HostImageRounded = styled.img`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${(props) => props.theme.borderRadius.round};

  width: ${rem(40)};
  height: ${rem(40)};
`

const FlashEventContent = styled(MarkdownRender)`
  padding: ${rem(16)} ${rem(20)} ${rem(20)};
`

const getVehicleIcon = switchFunc<typeof SvgIcon, App.Tours.VehicleType>({
  Coach: LineBusIcon,
  Bus: LineBusIcon,
  Car: LineCarIcon,
  Cruise: LineShipIcon,
  Flight: LinePlaneFlyIcon,
  Train: LineSubwayIcon,
  'Bullet Train': LineSubwayIcon,
  Ferry: LineShipIcon,
  Walking: LineWalkingIcon,
}, LineBusIcon)

interface Props {
  itineraryItem: App.Tours.TourV2OfferItineraryItem
  open: boolean
  toggleAccordion: (key: string) => void
  last: boolean
  shouldDisplayLocationsVisited?: boolean
  showExperiences: boolean
}

function TourV2ItineraryBreakdown(props: Props) {
  const { itineraryItem, open, toggleAccordion, last, shouldDisplayLocationsVisited, showExperiences } = props
  const intl = useIntl()
  const displayTransports = false

  const locationsVisited = useMemo(
    () => shouldDisplayLocationsVisited ? itineraryItem.locationsVisited?.join(', ') : undefined,
    [itineraryItem.locationsVisited, shouldDisplayLocationsVisited],
  )

  const [flashSpecialEvents, specialEvents] = useMemo<[Array<App.Tours.V2OfferSpecialEvent>, Array<App.Tours.V2OfferSpecialEvent>]>(
    () => partitionBy(itineraryItem.specialsEvents, (specialEvent) => specialEvent.isFlash),
    [itineraryItem.specialsEvents])

  const daysLabelText = useMemo(() => {
    if (itineraryItem.duration === 1) {
      return itineraryItem.startDay
    }

    return Array
      .from({ length: itineraryItem.duration }, (_, i) => itineraryItem.startDay + i) // Creates the days array
      .join(', ') // Joins then with a comma
      .replace(/, ([^,]*)$/, ' & $1') // Replaces the last comma with an ampersand
  }, [itineraryItem])

  const durationText = useMemo(() => {
    if (last) {
      return intl.formatMessage({
        id: '6NeWK9aJ7G',
        defaultMessage: 'Last day',
        description: 'Label indicating the final day of the tour',
      })
    }
    return intl.formatMessage({
      id: 'fqwihVggSu',
      defaultMessage: '{count, plural, one {One night} other {# nights}}',
      description: 'Duration in nights for itinerary item',
    }, { count: itineraryItem.duration })
  }, [last, itineraryItem.duration, intl])

  return (
    <AccordionSection>
      <AccordionHeader className={clsx({ open })} onClick={() => toggleAccordion(`${itineraryItem.startDay}`)}>

        <Group direction="horizontal" horizontalAlign="center">
          <TitleWrapper>
            <DayLabel variant="dark">{intl.formatMessage(
              {
                id: 'By8MwigrTh',
                defaultMessage: 'Day {days}',
                description: 'Label showing which day(s) of the tour itinerary',
              },
              { days: daysLabelText },
            )}
            </DayLabel>
            <DurationWrapper direction="vertical" verticalAlign="center">
              <BodyText variant="small">
                {durationText}
              </BodyText>
            </DurationWrapper>
            <Title variant="heading5">{itineraryItem.title}</Title>
          </TitleWrapper>
        </Group>

        <ItineraryIcon className={clsx({ open })} />
      </AccordionHeader>

      <Expander expanded={open}>
        <DescriptionWrapper direction="horizontal">
          <VisualAccentWrapper>
            <CircleWrapper>
              <svg width={BASE_RADIUS * 2} height={BASE_RADIUS * 2}>
                <StopCircle
                  cx={BASE_RADIUS}
                  cy={BASE_RADIUS}
                  r={BASE_RADIUS - STROKE_WIDTH}
                  strokeWidth={STROKE_WIDTH}
                />
              </svg>
            </CircleWrapper>
            <DashedLine />
          </VisualAccentWrapper>

          <PaneBodyStyled>
            <VerticalSpacer gap={16}>
              {!!locationsVisited && <Heading variant="heading5">{locationsVisited}</Heading>}
              <MarkdownRender content={itineraryItem.description} fontSize="medium" />
              {!!flashSpecialEvents.length &&
                <CardCarousel width={`min(${flashSpecialEvents.length === 1 ? '100%' : rem(270)}, 100%)`}>
                  {flashSpecialEvents.map((flashEvent, index) => (
                    <LabeledSheet
                      key={index}
                      kind="exclusive"
                      label={intl.formatMessage({
                        id: 'mQ1bjz0nIM',
                        defaultMessage: 'Limited time inclusions',
                        description: 'Label for exclusive limited-time tour inclusions',
                      })}
                    >
                      <FlashEventContent fontSize="medium" content={flashEvent.description} />
                    </LabeledSheet>
                  ))}
                </CardCarousel>}
              {!!specialEvents.length &&
                <VerticalSpacer gap={8}>
                  <Heading variant="heading6">{intl.formatMessage({
                    id: 'aYEOG4owDh',
                    defaultMessage: 'Signature highlights',
                    description: 'Section heading for special tour highlights and events',
                  })}
                  </Heading>
                  <VerticalSpacer gap={4}>
                    {itineraryItem.specialsEvents.map((specialEvent, index) => (
                      <SpecialEventWrapper direction="horizontal" gap={16} verticalAlign="center" key={index}>
                        { !!specialEvent.hostImageId &&
                          <HostImageRounded src={getImageUrl(specialEvent.hostImageId, IMAGE_HOST_CONFIG)} />}
                        <MarkdownRender content={specialEvent.description} fontSize="medium" />
                      </SpecialEventWrapper>
                    ))}
                  </VerticalSpacer>
                </VerticalSpacer>}
              {(!!itineraryItem.meals?.length || !!itineraryItem.accommodation) && (
                <VerticalSpacer gap={8}>
                  <Heading variant="heading6">{intl.formatMessage({
                    id: 'OyRtvlEiR9',
                    defaultMessage: 'Inclusions',
                    description: 'Section heading for tour inclusions like accommodation and meals',
                  })}
                  </Heading>
                  {!!itineraryItem.accommodation && (
                    <Group direction="horizontal" gap={8} verticalAlign="center" data-testid="accommodation">
                      <LineBedDoubleIcon size="S" />
                      <BodyText variant="medium">{itineraryItem.accommodation}</BodyText>
                    </Group>
                  )}
                  {!!itineraryItem.meals?.length && (
                    <Group direction="horizontal" gap={8} verticalAlign="center" data-testid="meals">
                      <LineRestaurantIcon size="S" />
                      <BodyText variant="medium">{[itineraryItem.meals.slice(0, -1).join(', '), itineraryItem.meals.slice(-1)].filter(item => !!item).join(' and ')}</BodyText>
                    </Group>
                  )}
                </VerticalSpacer>
              )}
              {displayTransports && !!itineraryItem.transports?.length && (
                <Group direction="vertical" gap={8}>
                  <Heading variant="heading6">{intl.formatMessage({
                    id: 'SrPJ2Xdme6',
                    defaultMessage: 'Transport',
                    description: 'Section heading for transportation details',
                  })}
                  </Heading>
                  <Group direction="vertical" gap={4}>
                    {itineraryItem.transports.map((transport, index) => {
                      const Icon = getVehicleIcon(transport.vehicle)
                      const distanceText = transport.distance > 0 ? `, ${transport.distance} km` : ''
                      return (
                        <Group direction="horizontal" gap={8} verticalAlign="center" key={index} data-testid="transport">
                          <Icon size="S" />
                          <BodyText variant="medium">{`${transport.vehicle} - ${transport.time} mins ${distanceText}`}</BodyText>
                        </Group>
                      )
                    })}
                  </Group>
                </Group>
              )}
              {showExperiences && !!itineraryItem.experiences?.length &&
                <VerticalSpacer gap={12}>
                  <div>
                    <Heading variant="heading6">{intl.formatMessage({
                      id: 'fZXfnWaH2K',
                      defaultMessage: 'Personalise your day with optional experiences',
                      description: 'Heading for optional tour experiences that can be added',
                    })}
                    </Heading>
                    <BodyText variant="medium">{intl.formatMessage({
                      id: 'nrtXItUy+r',
                      defaultMessage: 'Available when booking your tour. Select one per time slot.',
                      description: 'Description of how optional experiences can be selected during booking',
                    })}
                    </BodyText>
                  </div>
                  <CardCarousel width={`min(${rem(270)}, 100%)`}>
                    {itineraryItem.experiences.map((experience) => {
                      const title = intl.formatMessage(
                        {
                          id: '7zQqOJTgd4',
                          defaultMessage: 'Day {days}: {locations}',
                          description: 'Title for experience card showing day(s) and locations',
                        },
                        {
                          days: daysLabelText,
                          locations: itineraryItem.locationsVisited.join(' - '),
                        },
                      )
                      return <TourItineraryExperienceCard
                        key={experience.id}
                        tourExperience={experience}
                        title={title}
                      />
                    })}
                  </CardCarousel>
                </VerticalSpacer>}
            </VerticalSpacer>
          </PaneBodyStyled>
        </DescriptionWrapper>
      </Expander>
    </AccordionSection>
  )
}

export default memo(TourV2ItineraryBreakdown)
